import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box, Button, Chip, IconButton, Paper, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Tooltip, Typography, Pagination,
} from '@mui/material';
import { Visibility } from '@mui/icons-material';
import { SupplierPaymentService } from '../../services/supplierPayment.service';
import { SupplierPayment, SupplierPaymentStatus } from '../../interfaces/procurement/supplierPayment.interface';
import { useToast } from '../../context/ToastContext';
import { usePermission } from '../../hooks/usePermission';
import { PERMISSIONS } from '../../constants/permissions';
import { formatMoney, humanizeEnum } from '../../utils/format';
import { formatDate } from '../../utils/dateUtils';
import { formatFc, isForeignCurrency } from '../../utils/fcDocuments'; // G5a polish

const STATUS_COLORS: Record<SupplierPaymentStatus, 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning'> = {
  POSTED: 'success',
  REVERSED: 'error',
};

export const SupplierPaymentList: React.FC = () => {
  const navigate = useNavigate();
  const canManage = usePermission(PERMISSIONS.SUPPLIER_PAYMENT_MANAGE); // recording a payment is SUPPLIER_PAYMENT_MANAGE server-side
  const toast = useToast();
  const [searchParams] = useSearchParams();
  const supplierBillId = searchParams.get('supplierBillId');

  const [payments, setPayments] = useState<SupplierPayment[]>([]);
  const [loading, setLoading] = useState(true);
  const [pageInfo, setPageInfo] = useState({ currentPage: 0, totalPages: 0, totalElements: 0 });

  const fetchPayments = async (page = 0) => {
    setLoading(true);
    try {
      const response = supplierBillId
        ? await SupplierPaymentService.listByBill(supplierBillId, page, 10)
        : await SupplierPaymentService.listByInstitution(page, 10);
      setPayments(response.content);
      setPageInfo({ currentPage: response.number, totalPages: response.totalPages, totalElements: response.totalElements });
    } catch (e) {
      toast.showApiError(e, 'Failed to load supplier payments');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPayments(0);
  }, [supplierBillId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handlePageChange = (_event: React.ChangeEvent<unknown>, page: number) => {
    fetchPayments(page - 1);
  };

  return (
    <Box sx={{ p: 3 }} data-help-page="procurement.paymentList.page">
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="h5">{supplierBillId ? 'Payments for Bill' : 'Supplier Payments'}</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {supplierBillId ? (
            <>
              {canManage && (
                <Button
                  data-help="procurement.paymentList.record"
                  variant="contained"
                  onClick={() => navigate(`/procurement/supplier-payments/new?supplierBillId=${supplierBillId}`)}
                >
                  Record Payment
                </Button>
              )}
              <Button variant="outlined" onClick={() => navigate(-1)}>Back</Button>
            </>
          ) : (
            <Button variant="outlined" onClick={() => navigate('/procurement/supplier-bills')}>
              Go to Supplier Bills
            </Button>
          )}
        </Box>
      </Box>

      {loading ? (
        <Typography>Loading...</Typography>
      ) : (
        <>
          <Typography variant="body2" sx={{ mb: 1 }}>Total: {pageInfo.totalElements}</Typography>
          <TableContainer component={Paper} data-help-section="procurement.paymentList.table">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell data-help="procurement.paymentList.col.number">Payment Number</TableCell>
                  <TableCell data-help="procurement.paymentList.col.bill">Bill</TableCell>
                  <TableCell data-help="procurement.paymentList.col.supplier">Supplier</TableCell>
                  <TableCell data-help="procurement.paymentList.col.date">Payment Date</TableCell>
                  <TableCell data-help="procurement.paymentList.col.amount">Amount</TableCell>
                  <TableCell data-help="procurement.paymentList.col.status">Status</TableCell>
                  <TableCell data-help="procurement.paymentList.col.method">Payment Method</TableCell>
                  <TableCell data-help="procurement.paymentList.col.actions">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {payments.map(payment => (
                  <TableRow key={payment.id}>
                    <TableCell>{payment.paymentNumber}</TableCell>
                    <TableCell>{payment.supplierBillNumber ?? '—'}</TableCell>
                    <TableCell>{payment.supplierName ?? '—'}</TableCell>
                    <TableCell>{formatDate(payment.paymentDate)}</TableCell>
                    <TableCell>{formatMoney(payment.amount)}
                    {isForeignCurrency(payment.currencyCode) && payment.foreignAmount != null && (
                      <Typography variant="caption" display="block" color="text.secondary" data-testid="payment-foreign-amount">{formatFc(payment.currencyCode!, payment.foreignAmount)}</Typography>
                    )}
                    </TableCell>
                    <TableCell>
                      <Chip data-help="procurement.paymentList.status" label={humanizeEnum(payment.status)} color={STATUS_COLORS[payment.status]} size="small" />
                    </TableCell>
                    <TableCell>{payment.paymentMethod ? payment.paymentMethod : '-'}</TableCell>
                    <TableCell>
                      <Tooltip title="View" arrow>
                        <IconButton
                          data-help="procurement.paymentList.action.view"
                          size="small"
                          color="primary"
                          onClick={() => navigate(`/procurement/supplier-payments/${payment.id}`)}
                          aria-label="View"
                        >
                          <Visibility />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {pageInfo.totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
              <Pagination
                count={pageInfo.totalPages}
                page={pageInfo.currentPage + 1}
                onChange={handlePageChange}
                color="primary"
                showFirstButton
                showLastButton
              />
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

export default SupplierPaymentList;
